import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  message,
  Select,
  Dropdown,
  Menu,
  Tooltip,
} from "antd";
import { Link } from "react-router-dom";
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Table from "../../core/common/dataTable/index";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import { ColumnsType } from "antd/es/table";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../../core/api/products";
import { createModule, deleteModule } from "../../core/api/useModules";
import { toCamelCase } from "../../core/api/toCamelCase";
import { toast } from "react-toastify";
import { usePermission } from "../../core/api/useRolePermission";
import { useAuthToken } from "../../core/data/redux/useAuthToken";

interface Product {
  key: string;
  product_id?: number;
  product_name: string;
  description?: string;
  modules?: { module_name: string; module_id?: number }[];
}

interface ProductDropdownOption {
  label: string;
  value: number;
}

const Products: React.FC = () => {
  const empToken = useAuthToken();
  const routes = all_routes;
  const [form] = Form.useForm();
  const [moduleForm] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editProductKey, setEditProductKey] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [isAddModuleModalVisible, setIsAddModuleModalVisible] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  );
  const [selectedProductName, setSelectedProductName] = useState<string | null>(
    null
  );

  // Permissions: Using the corrected usePermission hook
  const canCreate = usePermission("Products", "create");
  const canUpdate = usePermission("Products", "write");
  const canDelete = usePermission("Products", "delete");
  const canRead = usePermission("Products", "read");

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await fetchProducts(empToken);
      const formatted = data.map((item: any) => ({
        key: item.product_id.toString(),
        product_id: item.product_id,
        product_name: item.product_name,
        description: item.description,
        modules: item.modules || [],
      }));
      setProducts(formatted);
    } catch (error) {
      console.error("Failed to fetch products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleAddProduct = () => {
    if (!canCreate)
      return toast.warning("You don't have permission to create products.");
    setIsModalVisible(true);
    setEditProductKey(null);
    form.resetFields();
  };

  const handleClose = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleEditProduct = (record: Product) => {
    if (!canUpdate)
      return toast.warning("You don't have permission to edit products.");
    setIsModalVisible(true);
    setEditProductKey(record.product_id || null);
    form.setFieldsValue({
      product_name: record.product_name,
      description: record.description,
    });
  };

  const handleDeleteProduct = (productId: number) => {
    if (!canDelete)
      return toast.warning("You don't have permission to delete products.");
    setDeleteProductId(productId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (deleteProductId === null) return;

    try {
      await deleteProduct(empToken, deleteProductId);
      setDeleteModalVisible(false);
      setDeleteProductId(null);
      loadProducts();
    } catch (error: any) {
      console.error("Delete error →", error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.product_name || !values.product_name.trim()) {
        toast.warning("Product name is required");
        return;
      }

      if (!editProductKey && !canCreate) {
        toast.warning("You don't have permission to create products.");
        return;
      }
      if (editProductKey && !canUpdate) {
        toast.warning("You don't have permission to update products.");
        return;
      }

      const productData = {
        product_name: values.product_name.trim(),
        description: values.description?.trim() || "",
      };

      if (editProductKey) {
        await updateProduct(empToken, editProductKey, productData);
      } else {
        await createProduct(empToken, productData);
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditProductKey(null);
      loadProducts();
    } catch (error: any) {
      console.error("Submit error →", error);
    }
  };

  const filteredData = useMemo(() => {
    return products.filter((product) => {
      const search = searchText.toLowerCase();
      const nameMatch = product.product_name.toLowerCase().includes(search);
      const descMatch = product.description?.toLowerCase().includes(search);
      const moduleMatch = product.modules?.some((mod) =>
        mod.module_name.toLowerCase().includes(search)
      );
      return nameMatch || descMatch || moduleMatch;
    });
  }, [products, searchText]);

  const productOptions: ProductDropdownOption[] = products.map((product) => ({
    label: product.product_name,
    value: Number(product.product_id),
  }));

  const columns: ColumnsType<Product> = useMemo(
    () => [
      {
        title: "S.No",
        key: "serial",
        width: 70,
        align: "center",
        render: (_: any, __: Product, index: number) => (
          <strong>{index + 1 + (currentPage - 1) * pageSize}</strong>
        ),
      },
      {
        title: "Product Name",
        dataIndex: "product_name",
        key: "product_name",
        sorter: (a, b) => a.product_name.localeCompare(b.product_name),
        render: (text: string) => (
          <span style={{ fontWeight: 500 }}>{toCamelCase(text)}</span>
        ),
      },
      {
        title: "Module",
        dataIndex: "modules",
        key: "modules",
        sorter: (a: any, b: any) =>
          (a.modules?.length || 0) - (b.modules?.length || 0),
        render: (modules: Array<{ module_name: string }> = []) => (
          <ul style={{ padding: 0, margin: 0, listStyleType: "none" }}>
            {modules.length > 0 ? (
              modules.map((module, index) => (
                <li key={index} style={{ margin: "5px 0" }}>
                  {module.module_name.charAt(0).toUpperCase() +
                    module.module_name.slice(1)}
                </li>
              ))
            ) : (
              <li style={{ fontStyle: "italic", color: "#aaa" }}>
                No modules available
              </li>
            )}
          </ul>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        sorter: (a, b) =>
          (a.description || "").localeCompare(b.description || ""),
        render: (desc?: string) => (
          <div
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxWidth: 300,
              fontStyle: desc ? "normal" : "italic",
              color: desc ? "#333" : "#aaa",
            }}
          >
            {desc || "No description"}
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        render: (_: any, record: Product) => (
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditProduct(record)}
            />
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                if (record.product_id === undefined) {
                  message.error("Invalid product ID");
                  return;
                }
                handleDeleteProduct(record.product_id);
              }}
            />
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  const handleAddModuleSubmit = async () => {
    try {
      const values = await moduleForm.validateFields();
      if (!selectedProductId) {
        message.warning("Please select a product first");
        return;
      }

      if (!values.module_name || !values.module_name.trim()) {
        message.error("Module name is required");
        return;
      }

      await createModule(empToken, {
        product_id: selectedProductId,
        module_name: values.module_name.trim(),
        description: values.description?.trim() || "",
      });
      setIsAddModuleModalVisible(false);
      moduleForm.resetFields();
      setSelectedProductId(null);
      setSelectedProductName(null);
      await loadProducts();
    } catch (error: any) {
      console.error("Add module error →", error);
    }
  };

  const handleCloseAddModule = () => {
    setIsAddModuleModalVisible(false);
    moduleForm.resetFields();
    setSelectedProductId(null);
    setSelectedProductName(null);
  };

  const handleResetAddModule = () => {
    moduleForm.resetFields();
    setSelectedProductId(null);
    setSelectedProductName(null);
  };

  const [isDeleteModuleModalVisible, setIsDeleteModuleModalVisible] =
    useState(false);

  const moduleMenu = (
    <Menu>
      <Menu.Item key="add" onClick={() => setIsAddModuleModalVisible(true)}>
        ➕ Add Module
      </Menu.Item>
      <Menu.Item
        key="delete"
        onClick={() => setIsDeleteModuleModalVisible(true)}
      >
        ❌ Delete Module
      </Menu.Item>
    </Menu>
  );

  const [deleteModuleForm] = Form.useForm();

  const [selectedDeleteProductId, setSelectedDeleteProductId] = useState<
    number | null
  >(null);
  const [deleteModuleOptions, setDeleteModuleOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const handleDeleteModuleSubmit = async () => {
    try {
      const values = await deleteModuleForm.validateFields();
      const moduleId = values.module_id;
      if (!moduleId) {
        toast.warning("Please select a module to delete");
        return;
      }

      await deleteModule(empToken, moduleId);
      setIsDeleteModuleModalVisible(false);
      deleteModuleForm.resetFields();
      setSelectedDeleteProductId(null);
      setDeleteModuleOptions([]);

      await loadProducts();
    } catch (error: any) {
      console.error("Failed to delete module:", error);
    }
  };

  const handleCloseDeleteModule = () => {
    setIsDeleteModuleModalVisible(false);
    deleteModuleForm.resetFields();
    setSelectedDeleteProductId(null);
    setDeleteModuleOptions([]);
  };

  const handleResetDeleteModule = () => {
    deleteModuleForm.resetFields();
    setSelectedDeleteProductId(null);
    setDeleteModuleOptions([]);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Products</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Products
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={handleAddProduct}
                className="btn btn-primary d-flex align-items-center"
              >
                Add Product
              </Button>

              <Dropdown overlay={moduleMenu} trigger={["click"]}>
                <Button
                  type="primary"
                  className="btn btn-primary d-flex align-items-center"
                >
                  Modules <DownOutlined />
                </Button>
              </Dropdown>

              <div>
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Products</h5>
              <Input.Search
                placeholder="Search name..."
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 300 }}
                value={searchText}
              />
            </div>
            <div className="card-body p-0">
              <Table
                dataSource={filteredData}
                columns={columns}
                loading={loading}
                rowKey="product_id"
              />
            </div>
          </div>

          <Modal
            title={editProductKey ? "Edit Product" : "Add Product"}
            open={isModalVisible}
            onCancel={handleClose}
            footer={[
              <Button key="reset" onClick={handleReset}>
                Reset
              </Button>,
              <Button key="submit" type="primary" onClick={handleSubmit}>
                {editProductKey ? "Update" : "Add"}
              </Button>,
            ]}
            closeIcon={
              <Tooltip title="Close" placement="left">
                <span>
                  <CloseOutlined />
                </span>
              </Tooltip>
            }
            zIndex={9999} // make sure modal is above sidebar
            maskStyle={{
              backgroundColor: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(4px)", // 🔥 blur effect
            }}
          >
            <Form form={form} layout="vertical">
              <Form.Item
                label="Product Name"
                name="product_name"
                rules={[
                  { required: true, message: "Please enter the product name" },
                  { whitespace: true, message: "Product name cannot be empty" },
                ]}
              >
                <Input placeholder="Enter product name" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[
                  { required: true, message: "Please enter the description" },
                  { whitespace: true, message: "Description cannot be empty" },
                ]}
              >
                <Input.TextArea
                  placeholder="Enter description"
                  autoSize={{ minRows: 2 }}
                />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="Add Modules"
            open={isAddModuleModalVisible}
            onCancel={handleCloseAddModule}
            footer={[
              // <Button key="cancel" onClick={handleCloseAddModule}>
              //   Cancel
              // </Button>,
              <Button key="reset" onClick={handleResetAddModule}>
                Reset
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleAddModuleSubmit}
              >
                Save
              </Button>,
            ]}
            zIndex={9999} // make sure modal is above sidebar
            maskStyle={{
              backgroundColor: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(4px)", // 🔥 blur effect
            }}
          >
            <Form form={moduleForm} layout="vertical">
              <Form.Item
                label="Product Name"
                name="product_id"
                rules={[{ required: true, message: "Please select a product" }]}
              >
                <Select
                  showSearch
                  placeholder="Select a product"
                  value={selectedProductId ?? undefined}
                  onChange={(value, option) => {
                    setSelectedProductId(value);
                    setSelectedProductName((option as any)?.label ?? null);
                    moduleForm.setFieldsValue({ product_id: value });
                  }}
                  options={productOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Module Name"
                name="module_name"
                rules={[
                  { required: true, message: "Please enter module name" },
                  { whitespace: true, message: "Module name cannot be empty" },
                ]}
              >
                <Input placeholder="Enter module name" />
              </Form.Item>

              <Form.Item label="Description" name="description">
                <Input.TextArea
                  placeholder="Enter description (optional)"
                  autoSize={{ minRows: 2 }}
                />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="Confirm Deletion"
            open={deleteModalVisible}
            onOk={confirmDelete}
            onCancel={() => {
              setDeleteModalVisible(false);
              setDeleteProductId(null);
            }}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <p>Are you sure you want to delete this product?</p>
          </Modal>

          <Modal
            title="Delete Module"
            open={isDeleteModuleModalVisible}
            onCancel={handleCloseDeleteModule}
            footer={[
              // <Button key="cancel" onClick={handleCloseDeleteModule}>
              //   Cancel
              // </Button>,
              <Button key="reset" onClick={handleResetDeleteModule}>
                Reset
              </Button>,
              <Button
                key="delete"
                type="primary"
                danger
                onClick={handleDeleteModuleSubmit}
              >
                Delete
              </Button>,
            ]}
          >
            <Form form={deleteModuleForm} layout="vertical">
              <Form.Item
                label="Product Name"
                name="product_id"
                rules={[{ required: true, message: "Please select a product" }]}
              >
                <Select
                  showSearch
                  placeholder="Select a product"
                  options={productOptions}
                  value={selectedDeleteProductId ?? undefined}
                  onChange={(value) => {
                    setSelectedDeleteProductId(value);
                    deleteModuleForm.setFieldsValue({ product_id: value });

                    const selectedProduct = products.find(
                      (p) => p.product_id === value
                    );
                    if (selectedProduct && selectedProduct.modules) {
                      const moduleOptionsFormatted =
                        selectedProduct.modules.map((mod, index) => ({
                          label: mod.module_name,
                          value: (mod as any)?.module_id || index,
                        }));
                      setDeleteModuleOptions(moduleOptionsFormatted);
                    } else {
                      setDeleteModuleOptions([]);
                    }

                    deleteModuleForm.setFieldsValue({ module_id: undefined });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Module Name"
                name="module_id"
                rules={[{ required: true, message: "Please select a module" }]}
              >
                <Select
                  placeholder="Select a module"
                  options={deleteModuleOptions}
                  disabled={!deleteModuleOptions.length}
                />
              </Form.Item>
            </Form>
          </Modal>

          <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <p className="mb-0">2024 - 2025 © IATT.</p>
            <p>
              Designed & Developed By{" "}
              <Link to="#" className="text-primary">
                IATT
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;
